import React, { useEffect, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

import CustomHeader from './CustomHeader';
import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';

import { useFileManagerContext } from 'modules/file_manager/context/FileManagerContext';
import { getFolders } from 'services/fileManager/fileManagerServices';
import { Controller, useForm } from 'react-hook-form';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import useUpdateFiles from 'modules/file_manager/hooks/useUpdateFiles';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';

function MoveItemModal() {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm();

  const { addToast } = useToast();
  const { fileBulkMove } = useUpdateFiles();

  const {
    modals,
    isGlobalFileManager,
    fileManagerFilters,
    isLeadFileManager,
    caseDetails,
    handleGetFiles,
    pageLimit,
    selectedRows,
    setSelectedRows,
    breadCrumbs,
    isClientFileManager,
  } = useFileManagerContext();
  const { showBulkMoveModal, setShowBulkMoveModal, isCopy } = modals;
  const [folderList, setFolderList] = useState([]);

  // Watch the selected case to update folders when it changes
  const selectedCase = watch('case');

  useEffect(() => {
    getFoldersHandler();
  }, []);

  // Update folders when case selection changes
  useEffect(() => {
    if (selectedCase) {
      getFoldersHandler();
    }
  }, [selectedCase]);

  const getFoldersHandler = () => {
    let params = '';
    // Use selected case or fallback to current case
    const caseId = selectedCase?.id || (isGlobalFileManager || isClientFileManager ? fileManagerFilters?.case : caseDetails?.case_id);
    params = `case_id=${caseId}`;

    getFolders(params)
      .then((response) => {
        let items = response.data;
        let folders_in_selected = selectedRows.filter((item) => item.entry_type === 'folder').map((item) => item.object_id);
        items = items.filter((item) => !folders_in_selected.includes(item.value));
        const dropdownList = items.filter((item) => Object.keys(item).length > 1);
        // option to move to root folder
        (breadCrumbs?.length !== 0 || selectedCase?.id !== caseDetails?.case_id) &&
          dropdownList?.push({ label: 'Root directory', value: 'root' });
        setFolderList(dropdownList);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const moveFileHandler = (data) => {
    // Only check folder conflict if folder is selected and case hasn't changed
    if (data?.folder === fileManagerFilters?.selectedFolder && !isCaseChanged()) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant.message.FILE_MOVE_ERROR);
      return;
    }

    let file_ids = selectedRows.map((item) => {
      return {
        pk: item.pk,
        sk: item.sk,
      };
    });
    // Use selected case or fallback to current case
    const targetCaseId = data.case?.id || (isGlobalFileManager || isClientFileManager ? fileManagerFilters?.case : caseDetails?.case_id);

    let payload = {
      file_ids,
      case_id: targetCaseId,
      folder_id: data.folder === 'root' ? '' : data.folder,
    };

    if (isCopy) {
      payload.action = 'copy';
    }

    fileBulkMove(payload, 'move', isLeadFileManager)
      .then(() => {
        handleGetFiles(pageLimit);
        setShowBulkMoveModal(false);
        setSelectedRows([]);
        addToast(
          false,
          toastConstant.toasterType.SUCCESS,
          toastConstant.api.SUCCESS,
          isCopy ? toastConstant.message.FILE_COPY_SUCCESS : toastConstant.message.FILE_MOVED_SUCCESS
        );
      })
      .catch((error) => {
        console.log(error);
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.ERROR,
          isCopy ? toastConstant.message.FILE_COPY_FAILED : toastConstant.message.FILE_MOVE_FAILED
        );
      });
  };

  const moveFooter = (
    <div className="footer-btn">
      <Button label="Cancel" onClick={() => setShowBulkMoveModal(false)} className="p-button p-button-secondary outline mx-2" />
      <Button label={isCopy ? 'Copy' : 'Move'} onClick={handleSubmit(moveFileHandler)} className="p-button p-button-secondary" />
    </div>
  );

  // Create default case option with actual case name
  const getCurrentCaseOption = () => {
    // Prioritize caseDetails if available
    if (caseDetails?.case_id) {
      return {
        id: caseDetails.case_id,
        label: caseDetails.case_description.case_name,
        value: caseDetails.case_id,
      };
    }

    // Fallback to fileManagerFilters for global/client file manager
    if ((isGlobalFileManager || isClientFileManager) && fileManagerFilters?.case && fileManagerFilters.case.label) {
      return {
        id: fileManagerFilters.case.id,
        label: fileManagerFilters.case.label,
        value: fileManagerFilters.case.id,
      };
    }

    // Return empty if no case data available
    return {
      id: '',
      label: '',
      value: '',
    };
  };

  const currentCaseOption = getCurrentCaseOption();

  // Check if case has changed from the original
  const isCaseChanged = () => {
    const originalCaseId = currentCaseOption.id;
    const selectedCaseId = selectedCase?.id;
    return originalCaseId && selectedCaseId && originalCaseId !== selectedCaseId;
  };

  // Dynamic validation rules for folder
  const getFolderValidationRules = () => {
    return {
      required: !isCaseChanged(), // Only required if case hasn't changed
    };
  };

  return (
    <Dialog
      className="new-task-popup"
      header={<CustomHeader onHide={() => setShowBulkMoveModal(false)} />}
      footer={moveFooter}
      visible={showBulkMoveModal}
      style={{ width: '40vw' }}
      modal
      onHide={() => setShowBulkMoveModal(false)}
      closable={false}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="modal-heading F-size16 text-bold">{isCopy ? 'Copy' : 'Move'} Files</div>
      </div>
      <div className="P-medium">
        {isCopy ? 'Copy' : 'Move'} selected {selectedRows.length} files to
      </div>
      <br />

      {/* Case Selection Field */}
      <div className="mb-3">
        <label className="form-label F-size14 mb-2">Target Case/Lead</label>
        <Controller
          name="case"
          control={control}
          defaultValue={currentCaseOption}
          rules={{ required: true }}
          render={({ field }) => (
            <CaseListDropDown
              {...field}
              onChange={(selectedOption) => {
                field.onChange(selectedOption);
              }}
              placeholder="Select Case"
              disabled={false}
            />
          )}
        />
        {errors?.case && <InputErrorMessage>Select case</InputErrorMessage>}
      </div>

      {/* Folder Selection Field */}
      <div className="mb-3">
        <label className="form-label F-size14 mb-2">Target Folder</label>
        <Controller
          name="folder"
          control={control}
          defaultValue=""
          rules={getFolderValidationRules()}
          render={({ field }) => (
            <Dropdown
              className="input-shadow mb-2 mt-2 w-100 F-size14"
              placeholder={'Target Folder'}
              onChange={(e) => {
                field.onChange(e.value);
              }}
              id={field.name}
              value={field.value}
              options={folderList}
              isSearchable
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          )}
        />
        {errors?.folder && !isCaseChanged() && <InputErrorMessage>Select folder</InputErrorMessage>}
      </div>
    </Dialog>
  );
}

export default MoveItemModal;
